import { useEffect, useRef, useState } from 'react';
import { Layout } from '@/components/layout';
import { Card, Button, Spinner, Badge } from '@/components/atoms';
import { useBackButton } from '@/hooks';
import { verification } from '@/api';
import type { VerificationStatus } from '@/types/domain';
import { toast } from '@/components/atoms/Toast';
import { logger } from '@/utils/logger';
import styles from './pages.module.css';

export function VerificationPage(): JSX.Element {
  useBackButton();
  const [status, setStatus] = useState<VerificationStatus | null>(null);
  const [reason, setReason] = useState<string | undefined>();
  const passportRef = useRef<HTMLInputElement | null>(null);
  const selfieRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await verification.fetchVerificationStatus();
        setStatus(r.status);
        setReason(r.rejected_reason);
      } catch (err) {
        logger.warn('Verification status fetch failed', err);
        setStatus('none');
      }
    })();
  }, []);

  const submit = async (): Promise<void> => {
    const passport = passportRef.current?.files?.[0];
    const selfie = selfieRef.current?.files?.[0];
    if (!passport || !selfie) {
      toast.error('Обери обидва файли');
      return;
    }
    try {
      await verification.submitVerification(passport, selfie);
      setStatus('pending');
      toast.success('Подано на перевірку');
    } catch (err) {
      logger.error('Verification submit failed', err);
      toast.error('Не вдалось завантажити');
    }
  };

  if (status === null)
    return (
      <Layout title="Верифікація">
        <Spinner center />
      </Layout>
    );

  return (
    <Layout title="Верифікація">
      <Card>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
          <strong>Статус:</strong>
          {status === 'approved' ? <Badge variant="success">Верифіковано</Badge> : null}
          {status === 'pending' ? <Badge variant="warning">На розгляді</Badge> : null}
          {status === 'rejected' ? <Badge variant="danger">Відхилено</Badge> : null}
          {status === 'none' ? <Badge>Не подано</Badge> : null}
        </div>
        <p className={styles.note}>
          Верифікація — бейдж і вища позиція в пошуку. Документи зберігаються в захищеному сховищі.
        </p>
        {reason ? <p className={styles.note}>Причина відмови: {reason}</p> : null}
      </Card>

      {(status === 'none' || status === 'rejected') && (
        <Card>
          <div className={styles.formStack}>
            <label>
              Паспорт / ID-картка
              <input ref={passportRef} type="file" accept="image/*" />
            </label>
            <label>
              Селфі з документом
              <input ref={selfieRef} type="file" accept="image/*" />
            </label>
            <Button onClick={submit} fullWidth>
              Надіслати на перевірку
            </Button>
          </div>
        </Card>
      )}
    </Layout>
  );
}
